/**
 * Phase 16 Stage 5C — Company-Workspace live brand context.
 *
 * In-memory React provider. Wraps ONLY the authenticated Company
 * routes (mounted by <CompanyAuthenticatedShell/>). NEVER persists
 * the resolved payload to localStorage, sessionStorage, IndexedDB,
 * or any other surface — this is a strict rule of the locked Stage
 * 5C plan to prevent cross-Company brand leakage.
 *
 * Lifecycle:
 *   - Fetch on initial mount when both user.id and company.id are
 *     known.
 *   - Clear + refetch when user.id or company.id transition.
 *   - Synchronously clear on logout (provider unmounts → state
 *     gone before next route paints).
 *   - Throttled (60 s) refetch on focus / visibilitychange === visible.
 *   - On 5xx / network failure → mode='ordly', UI fully functional;
 *     manual retry exposed via refresh().
 *   - ETag held in React memory; sent as If-None-Match on refetch.
 *
 * Build-time short-circuit:
 *   When `REACT_APP_COBRANDED_COMPANY_SHELL_ENABLED !== 'true'` at
 *   CRACO compile time, this provider synchronously serves mode='ordly'
 *   and never issues /api/company/brand-context. The backend flag
 *   is still authoritative, but this lets us prove the OFF-build via
 *   network observation.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { api } from "./auth";

// The shell flag is read at every check, so Webpack/CRACO inlines it
// at build time (constant-folded) AND tests can mutate the env var
// per-suite without forcing a module re-import.
function _shellFlagOn() {
    return process.env.REACT_APP_COBRANDED_COMPANY_SHELL_ENABLED === "true";
}

const VIS_REFETCH_THROTTLE_MS = 60 * 1000;

const ORDLY_FALLBACK = Object.freeze({
    mode: "ordly",
    brand: null,
    relationship: { managed_by_firm: false },
    platform: { powered_by_ordly: false },
});

const CompanyBrandCtx = createContext(null);

function applyBrandToRoot(brand) {
    try {
        const root = document.documentElement;
        if (brand) {
            root.style.setProperty("--brand-accent", brand.accent_hex);
            root.style.setProperty("--brand-on-accent", brand.on_accent_hex);
            root.style.setProperty(
                "--brand-accent-hover",
                brand.accent_hover_hex,
            );
            root.style.setProperty(
                "--brand-focus-ring",
                brand.accent_focus_ring_hex,
            );
            document.body.classList.add("brand-mode-firm");
        } else {
            root.style.removeProperty("--brand-accent");
            root.style.removeProperty("--brand-on-accent");
            root.style.removeProperty("--brand-accent-hover");
            root.style.removeProperty("--brand-focus-ring");
            document.body.classList.remove("brand-mode-firm");
        }
    } catch {
        // SSR / non-DOM environments — no-op.
    }
}

export function CompanyBrandProvider({ user, company, children }) {
    // Storage is React state only. NEVER touch localStorage / sessionStorage.
    const [ctx, setCtx] = useState(ORDLY_FALLBACK);
    const [loading, setLoading] = useState(_shellFlagOn());
    const [error, setError] = useState(false);
    // payloadOwner: identity (user.id, company.id) for which the
    // current ``ctx`` was authoritatively resolved. Stays null until
    // a successful fetch resolves; reset to null on identity transition
    // or clear(). The bridge / shared BrandProvider compares this to
    // the current identity to gate the no-flash skeleton — Company B
    // can never be painted with Company A's resolved payload
    // (contract §22 tenant isolation + §15 no-flash).
    const [payloadOwner, setPayloadOwner] = useState(null);

    // Internal: opaque ETag and the identity it belongs to.
    const etagRef = useRef(null);
    const ownerRef = useRef({ userId: null, companyId: null });
    const lastFetchedAtRef = useRef(0);
    const inFlightRef = useRef(false);

    const clear = useCallback(() => {
        etagRef.current = null;
        lastFetchedAtRef.current = 0;
        setCtx(ORDLY_FALLBACK);
        setError(false);
        setPayloadOwner(null);
        applyBrandToRoot(null);
    }, []);

    const fetchContext = useCallback(async (opts = {}) => {
        // Capture the identity this fetch is being issued for, so we
        // can stamp payloadOwner on the result.
        const fetchOwner = {
            userId: user?.id || null,
            companyId: company?.id || null,
        };
        if (!_shellFlagOn()) {
            // Build-time short-circuit: synchronous ordly, never call API.
            applyBrandToRoot(null);
            setCtx(ORDLY_FALLBACK);
            setLoading(false);
            setError(false);
            setPayloadOwner(fetchOwner);
            return;
        }
        if (!fetchOwner.userId || !fetchOwner.companyId) {
            applyBrandToRoot(null);
            setCtx(ORDLY_FALLBACK);
            setLoading(false);
            setPayloadOwner(null);
            return;
        }
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        const force = !!opts.force;
        try {
            const headers = {};
            if (etagRef.current && !force) {
                headers["If-None-Match"] = `"${etagRef.current}"`;
            }
            const res = await api.get("/company/brand-context", {
                headers,
                // Tell axios not to throw on 304 — we treat it as success.
                validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
            });
            lastFetchedAtRef.current = Date.now();
            setError(false);
            if (res.status === 304) {
                // Body unchanged — keep current ctx. The current
                // payloadOwner is, by construction, equal to fetchOwner.
                setLoading(false);
                setPayloadOwner(fetchOwner);
                return;
            }
            const newEtag = res.headers?.etag || res.headers?.ETag;
            if (newEtag && typeof newEtag === "string") {
                etagRef.current = newEtag.replace(/^"|"$/g, "");
            }
            const body = res.data || ORDLY_FALLBACK;
            // Defensive shape guard: anything other than the two known
            // modes collapses to ordly.
            if (body.mode === "firm_branded" && body.brand) {
                setCtx(body);
                applyBrandToRoot(body.brand);
            } else {
                setCtx(ORDLY_FALLBACK);
                applyBrandToRoot(null);
            }
            setPayloadOwner(fetchOwner);
            setLoading(false);
        } catch {
            // 5xx / network / timeout → ordly + error flag. Manual retry
            // via refresh() or visibility throttle. Mark the failure as
            // authoritatively owned by the issuing identity so the
            // bridge can open the safe-ordly fallback for that identity.
            setCtx(ORDLY_FALLBACK);
            applyBrandToRoot(null);
            setError(true);
            setLoading(false);
            setPayloadOwner(fetchOwner);
        } finally {
            inFlightRef.current = false;
        }
    }, [user?.id, company?.id]);

    // Initial bootstrap + identity-change clear.
    useEffect(() => {
        const prev = ownerRef.current;
        const next = { userId: user?.id || null, companyId: company?.id || null };
        if (prev.userId !== next.userId || prev.companyId !== next.companyId) {
            // Identity transition: clear synchronously, then refetch.
            clear();
            ownerRef.current = next;
            if (_shellFlagOn()) setLoading(true);
            // Schedule the fetch after the clear paints.
            fetchContext({ force: true });
        }
    }, [user?.id, company?.id, clear, fetchContext]);

    // Visibility / focus throttled refetch (NOT polling).
    useEffect(() => {
        if (!_shellFlagOn()) return undefined;
        const maybeRefetch = () => {
            if (!user?.id || !company?.id) return;
            if (document.visibilityState !== "visible") return;
            const since = Date.now() - lastFetchedAtRef.current;
            if (since < VIS_REFETCH_THROTTLE_MS) return;
            fetchContext();
        };
        window.addEventListener("focus", maybeRefetch);
        document.addEventListener("visibilitychange", maybeRefetch);
        return () => {
            window.removeEventListener("focus", maybeRefetch);
            document.removeEventListener("visibilitychange", maybeRefetch);
        };
    }, [user?.id, company?.id, fetchContext]);

    // Synchronous cleanup on unmount (logout / route exit).
    useEffect(() => {
        return () => {
            applyBrandToRoot(null);
        };
    }, []);

    const refresh = useCallback(() => fetchContext({ force: true }), [fetchContext]);

    const value = useMemo(
        () => ({
            mode: ctx.mode,
            brand: ctx.brand,
            relationship: ctx.relationship,
            platform: ctx.platform,
            // Stage 5E.2 envelope passthrough (additive; null on legacy
            // responses that did not include them — the shared
            // normaliser falls back to the brand.* flat keys).
            branding_mode: ctx.branding_mode || null,
            display_name: ctx.display_name || null,
            contact_email: ctx.contact_email || null,
            contact_phone: ctx.contact_phone || null,
            website: ctx.website || null,
            logos: ctx.logos || null,
            tokens: ctx.tokens || null,
            attribution: ctx.attribution || null,
            loading,
            error,
            refresh,
            shellFlagOn: _shellFlagOn(),
            // Identity ownership signals (contract §15 + §22). The
            // ``currentOwner`` is derived synchronously from the
            // props passed by <CompanyAuthenticatedShell/> so the
            // very first render after an identity transition sees the
            // new identity immediately. ``payloadOwner`` only updates
            // after a fetch authoritatively completes for that
            // identity. The bridge compares the two: any mismatch is
            // treated as unresolved, so Company B can never paint
            // with Company A's previously-resolved tokens or shell.
            currentOwner: {
                userId: user?.id || null,
                companyId: company?.id || null,
            },
            payloadOwner,
        }),
        [ctx, loading, error, refresh, payloadOwner, user?.id, company?.id],
    );

    return (
        <CompanyBrandCtx.Provider value={value}>
            {children}
        </CompanyBrandCtx.Provider>
    );
}

export function useCompanyBrand() {
    const v = useContext(CompanyBrandCtx);
    if (!v) {
        // Outside an authenticated Company shell → ordly defaults. Never
        // throws; consumers that render under both branded and non-branded
        // surfaces (e.g. shared components) stay safe.
        return {
            mode: "ordly",
            brand: null,
            relationship: { managed_by_firm: false },
            platform: { powered_by_ordly: false },
            loading: false,
            error: false,
            refresh: () => {},
            shellFlagOn: _shellFlagOn(),
            currentOwner: { userId: null, companyId: null },
            payloadOwner: { userId: null, companyId: null },
        };
    }
    return v;
}
